import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { FaLightbulb, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CharacterBubble from '../components/CharacterBubble';

function PitchPage() {
  const [topic, setTopic] = useState(null);
  const [angles, setAngles] = useState(['', '', '']);
  const [sources, setSources] = useState(['', '', '', '', '']);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopic();
  }, []);

  const fetchTopic = async () => {
    try {
      const response = await api.get('/pitch/topic');
      setTopic(response.data);
    } catch (error) {
      console.error('Error fetching topic:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/pitch/evaluate', {
        topic: topic.topic,
        angles: angles.filter(a => a.trim()),
        sources: sources.filter(s => s.trim())
      });

      setResult(response.data);
    } catch (error) {
      console.error('Error submitting pitch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAngles(['', '', '']);
    setSources(['', '', '', '', '']);
    setResult(null);
    fetchTopic();
  };

  if (!topic) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <header className="border-b-4 border-black" style={{ background: '#FB8500' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 comic-button bg-white text-black hover:bg-gray-100"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline" /> 
            <span className="hidden sm:inline">Retour Dashboard</span>
            <span className="sm:hidden">Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl comic-title text-white">
            <FaLightbulb className="inline mr-2" />
            Pitch d'Angles
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {!result ? (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="comic-panel"
            >
              <div className="mb-6">
                <div className="speech-bubble mb-4">
                  <p className="comic-text text-xl mb-2"><strong>Sujet d'actualité:</strong></p>
                  <p className="text-lg">{topic.topic}</p>
                  {topic.context && (
                    <p className="text-sm mt-2 text-gray-600">{topic.context}</p>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} data-testid="pitch-form">
                <div className="mb-6">
                  <h3 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>
                    Propose 3 angles
                  </h3>
                  {angles.map((angle, idx) => (
                    <div key={idx} className="mb-3">
                      <label className="block comic-text mb-2">Angle {idx + 1}</label>
                      <input
                        type="text"
                        value={angle}
                        onChange={(e) => {
                          const newAngles = [...angles];
                          newAngles[idx] = e.target.value;
                          setAngles(newAngles);
                        }}
                        className="w-full input-comic"
                        placeholder="Quel angle traiter?"
                        required
                        data-testid={`angle-input-${idx}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="mb-6">
                  <h3 className="comic-title text-2xl mb-4" style={{ color: '#E63946' }}>
                    Propose 5 sources à interviewer
                  </h3>
                  <p className="text-sm mb-3 text-gray-600">
                    <strong>Attention:</strong> Sois réaliste ! Elon Musk ou le Pape = peu réaliste. Expert, auteur, témoin = réaliste.
                  </p>
                  {sources.map((source, idx) => (
                    <div key={idx} className="mb-3">
                      <label className="block comic-text mb-2">Source {idx + 1}</label>
                      <input
                        type="text"
                        value={source}
                        onChange={(e) => {
                          const newSources = [...sources];
                          newSources[idx] = e.target.value;
                          setSources(newSources);
                        }}
                        className="w-full input-comic"
                        placeholder="Qui interviewer?"
                        required
                        data-testid={`source-input-${idx}`}
                      />
                    </div>
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full comic-button comic-button-red text-2xl py-4"
                  data-testid="submit-pitch-button"
                >
                  {loading ? 'ÉVALUATION...' : 'SOUMETTRE LE PITCH'}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="comic-panel text-center"
            >
              <div className="onomatopoeia mb-6">
                {result.score >= 80 ? 'EXCELLENT!' : result.score >= 60 ? 'BIEN!' : 'AMÉLIORE-TOI!'}
              </div>
              
              <div className="text-6xl mb-4" style={{ color: '#FFB703' }}>
                {result.score}/100
              </div>

              <CharacterBubble 
                context="pitch"
                customMessage={result.feedback}
                isCorrect={result.score >= 70}
                showAlways={true}
              />

              <button
                onClick={handleReset}
                className="comic-button comic-button-navy text-xl mt-6"
                data-testid="try-again-button"
              >
                Essayer un autre sujet
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PitchPage;
